/** Approved visits of one approval: saved captures paged over membership, never a live Visit query. */
import { inject, Injector } from '@angular/core';
import { ActivityEntityName, EntityViewDisplay, VisitApprovalVisitRow } from '@jms/model';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import type { EntityManifestBoardProfile } from '@workbuddy/services/entities/entity.manifest.surface';
import { OutlineEntityKinds } from '@workbuddy/surfaces/outline-authoring';
import { Command } from '@workbuddy/ui/model';
import { EntityBoardContext, resolveBoardParams } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { openApprovalSnapshot } from './commands/approval.open.snapshot.command';
import { removeFromApproval } from './commands/approval.remove.visits.command';
import { reviewApprovalChanges } from './commands/approval.review.changed.command';
import { viewCurrentApprovalVisit } from './commands/approval.view.current.command';
import { ApprovalEntryRowComponent } from './presentation/approval.entry.row/approval.entry.row.component';
import { ApprovalVisitsBoardParams } from './visit.approval.detail.model';

/** The shared outline row draws a flat approved visit; there is no schedule or tree behind it. */
const outlineEntityKinds: OutlineEntityKinds = {
  line: ActivityEntityName.Visit,
  task: ActivityEntityName.VisitApproval,
  outline: ActivityEntityName.VisitApproval,
  linePrefix: 'visit:',
  taskPrefix: 'approval:',
  lineRef: () => undefined,
  lineParam: 'visit',
  hasSchedule: false,
  datesVisits: false,
};

/**
 * A factory because every row action needs the mount's approval, scope and lock (`params`), and it
 * runs in the board's injector so the dialogs open from inside this tab.
 */
function approvalVisitsBoard(page: EntityBoardContext): EntityManifestBoardProfile {

  const injector = inject(Injector);
  const params = resolveBoardParams<ApprovalVisitsBoardParams>(page);
  return {
    supportedDisplays: [EntityViewDisplay.List],
    restrictions: { supportedDisplays: [EntityViewDisplay.List] },
    // Keyset under a snapshot token: a skip-based deep page would re-walk membership that a
    // concurrent amendment can move.
    paging: 'keyset',
    params: { readonly: true, outlineEntityKinds },
    header: {
      // The registered server view, so the tab never inherits an unrelated `isDefault` view.
      defaultViewId: 'VisitApprovalVisit-approved-embedded',
      showTitle: true,
      hideAdd: true,
      showSearch: false,
      showPaginator: true,
      showViewOptions: false,
      showBoardControls: false,
    },
    table: {
      fillerColumn: true,
      identifierLink: () => false,
      columns: {
        byField: {
          name: { minWidth: 190, flex: 1 },
          startDate: { minWidth: 220 },
        },
      },
    },
    list: {
      rowRenderer: ApprovalEntryRowComponent,
      highlightActiveRow: false,
      rowHeight: () => 40,
    },
    itemInteraction: {
      navigate: row => {
        if (params) void openApprovalSnapshot(injector, params, row);
      },
      commands: row => !params ? [] : [
        // Only the row's current decision keeps its capture; one a later revision replaced is gone, and the menu says so.
        row.membership
          ? new Command({ _id: 'snapshot', label: 'View approved snapshot', action: () => { void openApprovalSnapshot(injector, params, row); } })
          : new Command({ _id: 'snapshot', label: 'View approved snapshot', disabled: true, tooltip: 'Not kept: a later revision replaced this approval' }),
        new Command({ _id: 'current', label: 'View current visit', action: () => viewCurrentApprovalVisit(injector, row) }),
        ...(params.canRemove && row.membership ? [
          new Command({ _id: 'review', label: 'Review changed work…', action: () => { void reviewApprovalChanges(injector, params); } }),
          new Command({ _id: 'withdraw', label: 'Remove from approval…', disabled: params.busy(), action: () => { void removeFromApproval(injector, params, [row]); } }),
        ] : []),
      ],
    },
  };
}

export const visitApprovalVisitManifest = registerManifest<VisitApprovalVisitRow>({
  entity: ActivityEntityName.VisitApprovalVisit,
  board: {
    base: approvalVisitsBoard,
    profiles: {},
  },
});
